import moment from 'moment';
// Helpers
import { buildAudienceFilters } from './audienceFilters';
import {
  convertEngagementRateToOption,
  getEngagementRateValue,
} from './campaign';
import { getSocialBoostActivity } from './cbActivity';
import { getSelectableGenders } from './campaignEditModal';
import {
  FOLLOWING_OPTIONS_MAX,
  FOLLOWING_OPTIONS_MIN,
  getLocationsForApi,
} from './campaignEditModal';
// Types
import {
  CampaignSmPlatform,
  CampaignStartMode,
  FromToFilter,
  SearchSocialBoostCriteriaInput,
  UserRole,
} from 'api/graphql-global-types';
import {
  GetCampaignsV2_getCampaigns_entities,
  GetCampaignsV2_getCampaigns_entities_search,
} from 'api/campaignV2/types/GetCampaignsV2';
import { SocialBoostCampaignFormValues } from 'pages/Campaigns/components/SocialBoostCampaignEditModal/SocialBoostCampaignEditModal';

const ISO_DATETIME_RE = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z/g;

export const humanizeDatesInMessage = (
  message?: string | null
): string | undefined => {
  if (!message) return undefined;
  return message.replace(ISO_DATETIME_RE, (iso) =>
    moment(iso).format('MMM D, YYYY [at] h:mm A')
  );
};

export const getSocialBoostCampaignInitialFormValues = (
  record: GetCampaignsV2_getCampaigns_entities
): SocialBoostCampaignFormValues => {
  const activity = getSocialBoostActivity(record);
  const filters = record?.search as GetCampaignsV2_getCampaigns_entities_search;
  const initialPlatform = (filters as any)?.platformCriteria?.[0]?.platform as
    | CampaignSmPlatform
    | undefined;
  const initialSmPlatforms: CampaignSmPlatform[] = initialPlatform
    ? [initialPlatform]
    : [];

  const platformCriteria = (filters as any)?.platformCriteria?.[0];

  return {
    userRole: filters?.roles || [],
    budget: filters?.budget || 0,
    flatFeePerStore: record?.flatFeePerStore || 0,
    genders: getSelectableGenders(filters?.genders),
    maxAge: filters?.athleteMaxAge || null,
    minAge: filters?.athleteMinAge || null,
    smPlatforms: initialSmPlatforms,
    followingFrom:
      FOLLOWING_OPTIONS_MIN.find(
        (f) =>
          f.value === (platformCriteria?.minFollowers ?? filters?.minFollowers)
      )?.value || null,
    followingTo:
      FOLLOWING_OPTIONS_MAX.find(
        (f) =>
          f.value === (platformCriteria?.maxFollowers ?? filters?.maxFollowers)
      )?.value || null,
    sports: filters?.sports || [],
    brandName: activity?.brandName || '',
    website: activity?.website || '',
    campaignName: record?.name || '',
    internalName: record?.internalName || '',
    dueDate: record?.dueDate ? moment(record?.dueDate) : null,
    postUrl: activity?.postUrl || '',
    caption: activity?.caption || '',
    tags: activity?.tags?.join(', ') || '',
    linkStickerUrl: activity?.linkStickerUrl || '',
    linkStickerTitle: activity?.linkStickerTitle || '',
    startMode: record?.startMode ?? CampaignStartMode.ASAP,
    startAt: record?.startAt
      ? moment(record.startAt).format('YYYY-MM-DDTHH:mm')
      : null,
    locations: record?.search?.locations || [],
    hometownLocations: record?.search?.hometownLocations || [],
    excludedLocations: record?.search?.excludedLocations || [],
    engagementRate: convertEngagementRateToOption(
      platformCriteria?.minEngagementRate ?? record?.search?.engagementRateFrom
    ),
    searchHashtags: record?.search?.hashtags
      ? record.search.hashtags.map((tag, idx) => ({
          label: tag,
          value: String(idx),
          storeCount: 0,
        }))
      : [],
    audienceGender: filters?.audienceGender || null,
    audienceAgeRanges: filters?.audienceAgeRanges || [],
    audienceEthnicities: filters?.audienceEthnicities || [],
    audienceLocations: filters?.audienceLocations || [],
    audienceInterests: filters?.audienceInterests || [],
  };
};

export const getSocialBoostCampaignCommonFields = (
  values: SocialBoostCampaignFormValues,
  isTikTok?: boolean
): SearchSocialBoostCriteriaInput => {
  const {
    userRole,
    budget,
    genders,
    maxAge,
    minAge,
    followingFrom,
    followingTo,
    engagementRate,
    sports,
    hometownLocations,
    excludedLocations,
    searchHashtags,
    locations,
    audienceGender,
    audienceAgeRanges,
    audienceEthnicities,
    audienceLocations,
    audienceInterests,
  } = values;

  const criteria: SearchSocialBoostCriteriaInput = {};

  if (userRole) {
    criteria.roles = userRole.length
      ? userRole
      : [UserRole.Athlete, UserRole.ContentCreator];
  }
  if (budget) {
    criteria.budget = Number(budget);
  }
  if (genders) {
    criteria.genders = genders;
  }
  if (maxAge) {
    criteria.maxAge = Number(maxAge);
  }
  if (minAge) {
    criteria.minAge = Number(minAge);
  }
  if (sports) {
    criteria.sports = sports;
  }
  if (locations) {
    criteria.locations = locations;
  }
  if (hometownLocations) {
    criteria.hometownLocations = hometownLocations;
  }
  if (excludedLocations) {
    criteria.excludedLocations = getLocationsForApi(excludedLocations);
  }
  if (searchHashtags) {
    criteria.hashtagInputs = searchHashtags.map((hashtag) => ({
      name: hashtag.label.trim(),
    }));
  }

  // Social Boost targets a single platform (Instagram or TikTok) and the
  // per-platform follower / engagement bounds live under it. The backend
  // search validation REQUIRES a non-empty platformCriteria — without it the
  // search is considered invalid and returns zero talents.
  const followersRange: FromToFilter | undefined =
    followingFrom != null || followingTo != null
      ? {
          from: followingFrom != null ? Number(followingFrom) : null,
          to: followingTo != null ? Number(followingTo) : null,
        }
      : undefined;

  const engagementRateValue = getEngagementRateValue(engagementRate);
  const engagementRateRange: FromToFilter | undefined =
    engagementRateValue !== null
      ? { from: engagementRateValue, to: 1 }
      : undefined;

  criteria.platformCriteria = [
    {
      platform: isTikTok ? CampaignSmPlatform.TT : CampaignSmPlatform.IG,
      ...(followersRange && { followersRange }),
      ...(engagementRateRange && { engagementRateRange }),
    },
  ];

  const audienceFilters = buildAudienceFilters({
    audienceGender,
    audienceAgeRanges,
    audienceEthnicities,
    audienceLocations,
    audienceInterests,
    isTikTok,
  });

  if (audienceFilters) {
    criteria.audienceFilters = audienceFilters;
  }

  return criteria;
};
